/** Re-export of shared user provisioning logic (see convex/model/users). */
export * from "../../convex/model/users";

export type DemoStaffToggleVariant = "soft" | "outline";

export type DemoStaffToggleState =
  | { readonly kind: "idle" }
  | { readonly kind: "submitting"; readonly enabled: boolean }
  | { readonly kind: "failed"; readonly message: string };

export type DemoStaffToggleAction =
  | { readonly type: "started"; readonly enabled: boolean }
  | { readonly type: "succeeded" }
  | { readonly type: "failed"; readonly message: string }
  | { readonly type: "cleared" };

export const emptyDemoStaffToggleState = (): DemoStaffToggleState => ({ kind: "idle" });

export const nextDemoStaffToggleState = (
  _current: DemoStaffToggleState,
  action: DemoStaffToggleAction,
): DemoStaffToggleState => {
  switch (action.type) {
    case "started":
      return { kind: "submitting", enabled: action.enabled };
    case "succeeded":
    case "cleared":
      return { kind: "idle" };
    case "failed":
      return { kind: "failed", message: action.message };
  }
};

export const demoStaffToggleBusy = (state: DemoStaffToggleState): boolean => state.kind === "submitting";

export const demoStaffToggleError = (state: DemoStaffToggleState): string | null =>
  state.kind === "failed" ? state.message : null;

export type DemoStaffToggleViewInput = {
  readonly isStaff: boolean;
  readonly busy: boolean;
  readonly hasCompany: boolean;
};

export type DemoStaffToggleView = {
  readonly label: string;
  readonly title: string;
  readonly variant: DemoStaffToggleVariant;
  readonly disabled: boolean;
  readonly ariaPressed: boolean;
};

export type DemoStaffRedirectInput = {
  readonly enabled: boolean;
  readonly pathname: string;
};

export type DemoStaffRedirectRoute = "/alerts" | "/" | null;

const DEMO_STAFF_ROUTES = ["/alerts", "/admin", "/editor", "/imports", "/email-updates"] as const;

export const demoStaffToggleView = ({
  isStaff,
  busy,
  hasCompany,
}: DemoStaffToggleViewInput): DemoStaffToggleView => ({
  label: busy ? "Switching..." : isStaff ? "Admin view" : "Client view",
  title: isStaff
    ? "Currently in admin view. Switch to client view"
    : "Currently in client view. Switch to admin view",
  variant: isStaff ? "soft" : "outline",
  disabled: busy || !hasCompany,
  ariaPressed: isStaff,
});

export const demoStaffRedirectRoute = ({
  enabled,
  pathname,
}: DemoStaffRedirectInput): DemoStaffRedirectRoute => {
  if (enabled) return "/alerts";
  return DEMO_STAFF_ROUTES.some((route) => pathname.startsWith(route)) ? "/" : null;
};
